import { Box, Checkbox, Grid, SelectChangeEvent, Typography } from '@mui/material';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProceedSaveLater, SubHeading } from './components';
import { showError } from '../../redux-store/actions/auth';
import UseRadioGroup from '../../lib/formik/Radio';
import { MFTextField } from '../../lib/formik';
import { useHistory } from 'react-router';
import {
  IndividualAdditionalKYC,
  kycQuestionDetailType,
  investorKycQuestionMdsm,
  individualMdmsQuestionsKyc,
  nonIndividualQuestionsFatca,
  mdmsQuesAdditionKyc,
} from '../../redux-store/types/api-types';
import { RootStateType } from '../../redux-store/reducers';
import {
  applicationComparison,
  applyRoleBasedStatus,
  currencyConversion,
  getApplicantName,
  removeSingleQuote,
  saveForLater,
} from '../../utils/utilityFunctions';
import {
  getInvestorAdditionalKycQues,
  updateApplication,
} from '../../redux-store/actions/application';
import {
  AMC_APPROVER_CHECK_FOR_INDIVIDUAL,
  APPLICATION_TYPE,
  defaultCurrency,
} from '../../utils/constant';
import { useSnackbar } from 'notistack';
import MFCheckbox from '../../lib/formik/Checkbox';
import MFSelectField from '../../lib/formik/SelectField';
import { MultipleSelect } from '../../lib/formik/MultipleSelectField';
import { AdditionalKycDetailsValidation } from '../../utils/schema';
import updateVisibility from '../../utils/additionalKycUtils';
import { Gridstyles, Item, ItemDetails } from '../ApplicationDetails';
import { TextDatePicker } from '../../lib/formik/DatePickerWithTextFeild';
import MultiSelectCheckbox from '../../lib/formik/MultiCheckbox';

interface Values {
  applicants: {
    additional_kyc: IndividualAdditionalKYC[];
  }[];
  fundDeclaration: boolean;
  backgroundDeclaration: boolean;
  saveType: string;
}

export const AdditionalKycDeclarationForbackgroundText = () => {
  return (
    <>
      <Typography sx={{ fontSize: 16, fontWeight: 400, pt: 1, display: 'flex' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, pl: 2, pr: 1 }} component="span">
          •
        </Typography>
        I/we am/are not involved in any criminal, unlawful or money laundering activity and the
        above- mentioned funds as well as funds to be used in the future are clear, shall not be
        derived from or otherwise be connected with any activity which is illegal or unlawful;
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 400, pt: 1, display: 'flex' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, pl: 2, pr: 1 }} component="span">
          •
        </Typography>
        I am not currently insolvent and any transfer of assets involving the company will not
        render my estate or myself insolvent. I further declare that I have never been convicted in
        any court of law for a criminal offence or financial crime OR the Applicant has not been
        subject to any bankruptcy, liquidation or similar proceedings and that no orders have been
        issued against the Applicant by any legal or regulatory enforcement bodies;
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 400, pt: 1, display: 'flex' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, pl: 2, pr: 1 }} component="span">
          •
        </Typography>
        The Fund or the Investment Manager may disclose this information to law enforcement
        authorities when required by applicable laws and I/we shall inform them of any change herein
        and provide any additional information required by them;
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 400, pt: 1, display: 'flex' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, pl: 2, pr: 1 }} component="span">
          •
        </Typography>
        The information provided in this document is true, complete and correct. I/We agree to
        provide any further information or documentary evidence in respect of the funds upon request
        from the Fund.
      </Typography>
    </>
  );
};

export const setVisibilityFalse = (
  inputQId: string,
  q: Partial<IndividualAdditionalKYC>,
  formValues: investorKycQuestionMdsm,
  applicantIndex: number
): void => {
  if (q.id === inputQId && applicantIndex === q.applicantIndex) {
    q.isVisible = false;
    if (q.answer) {
      updateVisibility(q.answer, q, formValues, applicantIndex);
    }
  }
};

export function AdditionalKycDetails({
  data,
  values,
  setValues,
  applicantIndex,
  disable = false,
}: {
  data: IndividualAdditionalKYC[];
  values: Values;
  setValues: FormikHelpers<Values>['setValues'];
  applicantIndex: number;
  disable?: boolean;
}): JSX.Element {
  const { setFieldValue } = useFormikContext();
  return (
    <>
      {values.applicants[applicantIndex].additional_kyc.map((question, formValuesIndex) => {
        return (
          <>
            {question.isVisible && (
              <>
                {question.section_header && (
                  <Grid xs={12} sm={12}>
                    <SubHeading
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                      {`${question.section_header}`}{' '}
                    </SubHeading>
                  </Grid>
                )}
                {question.question_type === 'single_choice_radio' && (
                  <Grid item xs={12} sm={12} sx={{ pt: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        pb: 3,
                        pt: 1,
                        fontFamily: 'Work Sans,sans-serif',
                      }}>
                      <Typography
                        sx={{
                          pl: 1,
                          fontWeight: 400,
                          fontSize: 16,
                        }}>
                        {question.isMandatory === 'true'
                          ? question.question_text + ' *'
                          : question.question_text}
                      </Typography>
                    </Box>
                    <Grid xs={12}>
                      <UseRadioGroup
                        name={`applicants.${applicantIndex}.additional_kyc.${formValuesIndex}.answer`}
                        items={
                          question.options?.map((option) => ({
                            label: option.label,
                            value: option.key,
                          })) || []
                        }
                        onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                          const updatedValue = {
                            ...values,
                            applicants: values.applicants.map((applicant, index) =>
                              index === applicantIndex
                                ? {
                                    ...applicant,
                                    additional_kyc: applicant.additional_kyc.map((profile, i) =>
                                      i === formValuesIndex
                                        ? {
                                            ...profile,
                                            answer: value,
                                          }
                                        : profile
                                    ),
                                  }
                                : applicant
                            ),
                          };
                          setValues(updatedValue);
                          updateVisibility(
                            value,
                            question,
                            values.applicants[applicantIndex]
                              .additional_kyc as IndividualAdditionalKYC[],
                            applicantIndex
                          );
                        }}
                        disabled={disable}
                      />
                    </Grid>
                  </Grid>
                )}
                {question.question_type === 'textbox' && (
                  <Grid item xs={12} sm={question.fullWidth ? 12 : 6}>
                    <MFTextField
                      name={`applicants.${applicantIndex}.additional_kyc.${formValuesIndex}.answer`}
                      label={
                        question.question_text &&
                        (question.isMandatory === 'true'
                          ? question.question_text + ' *'
                          : `${question.question_text}`)
                      }
                      placeholder={question.placeholder || 'Please enter text here'}
                      disabled={disable}
                      multiline={question.multiline}
                      rows={question.rows}
                    />
                    {!isNaN(
                      Number(
                        values.applicants[applicantIndex].additional_kyc[formValuesIndex].answer
                      )
                    ) &&
                      question?.isDisplayCurrencyInWords && (
                        <Typography sx={{ fontSize: 13 }}>
                          {currencyConversion(
                            values.applicants[applicantIndex].additional_kyc[formValuesIndex]
                              .answer,
                            defaultCurrency
                          )}
                        </Typography>
                      )}
                  </Grid>
                )}
                {question.question_type === 'selectField' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <MFSelectField
                        name={`applicants.${applicantIndex}.additional_kyc.${formValuesIndex}.answer`}
                        label={
                          question.question_text &&
                          (question.isMandatory === 'true'
                            ? question.question_text + ' *'
                            : `${question.question_text}`)
                        }
                        items={
                          question?.options?.map((ele) => ({
                            key: ele.label,
                            value: ele.key,
                          })) || []
                        }
                        onChange={({ target: { value } }: SelectChangeEvent<unknown>) => {
                          setFieldValue(
                            `applicants.${applicantIndex}.additional_kyc.${formValuesIndex}.answer`,
                            value
                          );
                          updateVisibility(
                            value as string,
                            question,
                            values.applicants[applicantIndex]
                              .additional_kyc as IndividualAdditionalKYC[],
                            applicantIndex
                          );
                        }}
                        disabled={disable}
                      />
                    </Grid>
                  </>
                )}
                {question.question_type === 'multipleSelect' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      {!disable ? (
                        <MultipleSelect
                          name={`applicants.${applicantIndex}.additional_kyc.${formValuesIndex}.multiselectVal`}
                          label={
                            question.question_text &&
                            (question.isMandatory === 'true'
                              ? question.question_text + ' *'
                              : `${question.question_text}`)
                          }
                          items={
                            question?.options?.map((ele) => ({
                              key: ele.key,
                              value: ele.label,
                            })) || []
                          }
                          renderText="Selected Source of Funds"
                          onChange={({ target: { value } }: SelectChangeEvent<unknown>) => {
                            const items =
                              question?.options?.map((ele) => ({
                                key: ele.key,
                                value: ele.label,
                              })) || [];
                            const getFieldValue =
                              (value as string[] | number[])[
                                (value as string[] | number[])?.length - 1
                              ] === 'all'
                                ? (
                                    values.applicants[applicantIndex].additional_kyc[
                                      formValuesIndex
                                    ].multiselectVal as any
                                  )?.length === items.map((item) => item.key).length
                                  ? []
                                  : items.map((item) => item.key)
                                : value;
                            const updatedValues = {
                              ...values,
                              applicants: values.applicants.map((applicant, index) =>
                                index === applicantIndex
                                  ? {
                                      ...applicant,
                                      additional_kyc: applicant.additional_kyc.map((profile, i) =>
                                        i === formValuesIndex
                                          ? {
                                              ...profile,
                                              multiselectVal: getFieldValue as string[],
                                              answer: (getFieldValue as string[])
                                                ?.toString()
                                                .replace(/,/g, '*') as string,
                                            }
                                          : profile
                                      ),
                                    }
                                  : applicant
                              ),
                            };
                            setValues(updatedValues);
                            updateVisibility(
                              getFieldValue as string[],
                              question,
                              values.applicants[applicantIndex]
                                .additional_kyc as IndividualAdditionalKYC[],
                              applicantIndex
                            );
                          }}
                          disabled={disable}
                        />
                      ) : (
                        <Box sx={{ margin: '16px 0', padding: '0px' }}>
                          <Item sx={{ paddingTop: 0, marginBottom: 1 }}>
                            {question.question_text}
                          </Item>
                          <ItemDetails>
                            {values.applicants[applicantIndex].additional_kyc[
                              formValuesIndex
                            ].multiselectVal?.toString()
                              ? (
                                  values.applicants[applicantIndex].additional_kyc[formValuesIndex]
                                    .multiselectVal as string[]
                                )
                                  ?.map(
                                    (item: any) =>
                                      question?.options?.find((_item) => _item.key === item)?.label
                                  )
                                  ?.filter((ele) => ele)
                                  ?.join(', ')
                              : '-'}
                          </ItemDetails>
                        </Box>
                      )}
                    </Grid>
                  </>
                )}
                {question.question_type === 'multiSelectCheckBox' && (
                  <>
                    <Grid item xs={12} sm={12}>
                      <MultiSelectCheckbox
                        options={
                          question.options
                            ?.map((_ele) => ({
                              key: _ele.key,
                              value: _ele.label,
                            }))
                            .filter((ele) => ele) || []
                        }
                        onChange={(value: string) => {
                          const selectedValues =
                            (values.applicants[applicantIndex].additional_kyc[formValuesIndex]
                              .multiselectVal as string[]) || [];
                          const getFieldValue =
                            selectedValues.indexOf(value as string) === -1
                              ? [...selectedValues, value]
                              : selectedValues.filter((item: any) => item != (value as string));

                          const updatedValues = {
                            ...values,
                            applicants: values.applicants.map((applicant, index) =>
                              index === applicantIndex
                                ? {
                                    ...applicant,
                                    additional_kyc: applicant.additional_kyc.map((profile, i) =>
                                      i === formValuesIndex
                                        ? {
                                            ...profile,
                                            multiselectVal: getFieldValue as string[],
                                            answer: (getFieldValue as string[])
                                              ?.toString()
                                              .replace(/,/g, '*') as string,
                                          }
                                        : profile
                                    ),
                                  }
                                : applicant
                            ),
                          };
                          setValues(updatedValues);
                          updateVisibility(
                            getFieldValue as string[],
                            question,
                            values.applicants[applicantIndex]
                              .additional_kyc as IndividualAdditionalKYC[],
                            applicantIndex
                          );
                        }}
                        name={`applicants.${applicantIndex}.additional_kyc.${formValuesIndex}.multiselectVal`}
                        selectedValues={question.multiselectVal as string[]}
                        disabled={disable}
                      />
                    </Grid>
                  </>
                )}
                {question.question_type === 'datePicker' && (
                  <Grid item xs={12} sm={6}>
                    <TextDatePicker
                      name={`applicants.${applicantIndex}.additional_kyc.${formValuesIndex}.answer`}
                      label={
                        question.question_text &&
                        (question.isMandatory === 'true'
                          ? question.question_text + ' *'
                          : `${question.question_text}`)
                      }
                      inputLabelStyles={{
                        transform: 'unset',
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'rgba(0,0,0,0.7)',
                      }}
                      placeholder={question.placeholder || 'Please enter text here'}
                      disabled={disable}
                    />
                  </Grid>
                )}
              </>
            )}
          </>
        );
      })}
    </>
  );
}

export const setVisibility = (
  inputQId: string,
  q: Partial<IndividualAdditionalKYC>,
  formValues: investorKycQuestionMdsm,
  applicantIndex: number
): void => {
  if (q.id === inputQId && applicantIndex === q.applicantIndex) {
    q.isVisible = true;
    if (q.answer) {
      updateVisibility(q.answer, q, formValues, applicantIndex);
    }
  }
};

export const AssignAnsAndVisibilityForInitialInvestorQuestion = (
  kycQuestions: investorKycQuestionMdsm,
  existingInvestorQuestion: kycQuestionDetailType,
  applicantIndex: number
): any => {
  const assignAns = kycQuestions.map((question) => {
    const backendKey = question.backend_key as keyof kycQuestionDetailType;
    const existingAnswer = existingInvestorQuestion[backendKey] as string;

    if (!existingAnswer) {
      return question;
    }

    if (
      question.question_type === 'multiSelectCheckBox' ||
      question.question_type === 'multipleSelect'
    ) {
      return {
        ...question,
        multiselectVal: existingAnswer?.split('*') || null,
        answer: existingAnswer,
      };
    }
    return {
      ...question,
      answer: existingAnswer,
    };
  });

  const finalInitialFatcaQns = assignAns.map((q) => {
    if (
      ['single_choice_radio', 'multiSelectCheckBox', 'multipleSelect'].includes(q.question_type) &&
      q.answer &&
      q.answer.length > 0
    ) {
      updateVisibility(q.answer, q, assignAns, applicantIndex);
    }
    return q;
  });

  return finalInitialFatcaQns;
};
export default function additionalKYCDetail(): JSX.Element {
  const [kycDetails, setKycDetails] = useState<IndividualAdditionalKYC[]>([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { application } = useSelector((store: RootStateType) => store.application);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    (async function () {
      try {
        const response = (await dispatch(
          getInvestorAdditionalKycQues()
        )) as unknown as mdmsQuesAdditionKyc;
        setKycDetails(response.investor_kyc_question_individual);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, [application]);

  const {
    applicants = [],
    fundDeclaration = false,
    backgroundDeclaration = false,
  } = (application as any) || {};
  const initialValues: any = useMemo(() => {
    return {
      applicants: applicants.map((applicant: any, applicantIndex: number) => {
        const { additional_kyc: existingInvestorprofile = null } = applicant || {};
        return {
          additional_kyc: existingInvestorprofile
            ? AssignAnsAndVisibilityForInitialInvestorQuestion(
                kycDetails.map((_kyc_Details: IndividualAdditionalKYC) => ({
                  ..._kyc_Details,
                  applicantIndex: applicantIndex,
                })),
                existingInvestorprofile,
                applicantIndex
              )
            : kycDetails.map((_details) => ({
                ..._details,
                ...(_details?.multiselectVal && { multiselectVal: null }),
                applicantIndex: applicantIndex,
              })),
        };
      }),
      fundDeclaration: fundDeclaration || false,
      backgroundDeclaration: backgroundDeclaration || false,
      saveType: 'save and proceed',
    };
  }, [application, kycDetails]);

  const handleSubmit = async (values: Values) => {
    try {
      const { status } = application || {};
      const {
        applicants,
        saveType,
        backgroundDeclaration = false,
        fundDeclaration = false,
      } = values;
      const {
        applicants: exisitingApplicants = [],
        id,
        applicant1ReferenceId = '',
        currentStep,
        applicationType,
        applicationNumber,
        hasPOA,
      } = application || {};

      // if (!backgroundDeclaration || !fundDeclaration) {
      //   throw 'Please fill all the Declarations';
      // }
      const updatedApplicants = exisitingApplicants.map((applicant, index) => {
        const { additional_kyc = null } = applicants[index] || {};
        const { ...rest } = applicant.additional_kyc || {};
        const investorQuestionObj = additional_kyc?.reduce((prev, curr) => {
          return (
            curr.backend_key && {
              ...prev,
              [curr.backend_key as string]: curr.question_type === 'datePicker' ? null : '',
            }
          );
        }, {} as any) as kycQuestionDetailType;

        const finalProfileQuestion = (additional_kyc || []).reduce((prev, curr) => {
          if (curr.isVisible && curr.answer) {
            return { ...prev, [curr.backend_key as string]: curr.answer };
          }
          return prev;
        }, {}) as kycQuestionDetailType;

        return {
          ...applicant,
          additional_kyc: {
            ...rest,
            ...investorQuestionObj,
            ...finalProfileQuestion,
            id: applicant?.additional_kyc?.id || null,
            applicantId: applicant?.additional_kyc?.applicantId || null,
            isContributorEmployee: finalProfileQuestion.isContributorEmployee
              ? removeSingleQuote(finalProfileQuestion.isContributorEmployee)
              : investorQuestionObj.isContributorEmployee || null,
            subscribingAgentName: finalProfileQuestion.subscribingAgentName
              ? removeSingleQuote(finalProfileQuestion.subscribingAgentName)
              : investorQuestionObj.subscribingAgentName || null,
            isContributorForeignGovernment: finalProfileQuestion.isContributorForeignGovernment
              ? removeSingleQuote(finalProfileQuestion.isContributorForeignGovernment)
              : investorQuestionObj.isContributorForeignGovernment || null,
            foreignGovernmentName: finalProfileQuestion.foreignGovernmentName
              ? removeSingleQuote(finalProfileQuestion.foreignGovernmentName)
              : investorQuestionObj.foreignGovernmentName || null,
            foreignGovernmentPosition: finalProfileQuestion.foreignGovernmentPosition
              ? removeSingleQuote(finalProfileQuestion.foreignGovernmentPosition)
              : investorQuestionObj.foreignGovernmentPosition || null,
            foreignGovernmentRelation: finalProfileQuestion.foreignGovernmentRelation
              ? removeSingleQuote(finalProfileQuestion.foreignGovernmentRelation)
              : investorQuestionObj.foreignGovernmentRelation || null,
            isContributorSubscribingAgent: finalProfileQuestion.isContributorSubscribingAgent
              ? removeSingleQuote(finalProfileQuestion.isContributorSubscribingAgent)
              : investorQuestionObj.isContributorSubscribingAgent || null,
            subscribingAgentAddress: finalProfileQuestion.subscribingAgentAddress
              ? removeSingleQuote(finalProfileQuestion.subscribingAgentAddress)
              : investorQuestionObj.subscribingAgentAddress || null,
            personsHaveBeneficialInterest: finalProfileQuestion.personsHaveBeneficialInterest
              ? removeSingleQuote(finalProfileQuestion.personsHaveBeneficialInterest)
              : investorQuestionObj.personsHaveBeneficialInterest || null,
            personsHaveBeneficialInterestName:
              finalProfileQuestion.personsHaveBeneficialInterestName
                ? removeSingleQuote(finalProfileQuestion.personsHaveBeneficialInterestName)
                : investorQuestionObj.personsHaveBeneficialInterestName || null,
            personsHaveBeneficialInterestAddress:
              finalProfileQuestion.personsHaveBeneficialInterestAddress
                ? removeSingleQuote(finalProfileQuestion.personsHaveBeneficialInterestAddress)
                : investorQuestionObj.personsHaveBeneficialInterestAddress || null,
            doesContributionControlName: finalProfileQuestion.doesContributionControlName
              ? removeSingleQuote(finalProfileQuestion.doesContributionControlName)
              : investorQuestionObj.doesContributionControlName || null,
            doesContributionControlAddress: finalProfileQuestion.doesContributionControlAddress
              ? removeSingleQuote(finalProfileQuestion.doesContributionControlAddress)
              : investorQuestionObj.doesContributionControlAddress || null,
            employmentStatus: finalProfileQuestion.employmentStatus
              ? removeSingleQuote(finalProfileQuestion.employmentStatus)
              : investorQuestionObj.employmentStatus || null,
            employmentStatusName: finalProfileQuestion.employmentStatusName
              ? removeSingleQuote(finalProfileQuestion.employmentStatusName)
              : investorQuestionObj.employmentStatusName || null,
            employmentStatusBusiness: finalProfileQuestion.employmentStatusBusiness
              ? removeSingleQuote(finalProfileQuestion.employmentStatusBusiness)
              : investorQuestionObj.employmentStatusBusiness || null,
            employmentStatusAddress: finalProfileQuestion.employmentStatusAddress
              ? removeSingleQuote(finalProfileQuestion.employmentStatusAddress)
              : investorQuestionObj.employmentStatusAddress || null,
            employmentStatusNoOfEmployment: finalProfileQuestion.employmentStatusNoOfEmployment
              ? removeSingleQuote(finalProfileQuestion.employmentStatusNoOfEmployment)
              : investorQuestionObj.employmentStatusNoOfEmployment || null,
            employmentGrossAnnualIncome: finalProfileQuestion.employmentGrossAnnualIncome
              ? removeSingleQuote(finalProfileQuestion.employmentGrossAnnualIncome)
              : investorQuestionObj.employmentGrossAnnualIncome || null,
          },
        };
      });
      const checkApplication = applicationComparison(
        {
          ...application,
        },
        {
          ...application,
          applicants: updatedApplicants,
          backgroundDeclaration: false,
          fundDeclaration: false,
          currentStep: !!currentStep && currentStep > 3 ? currentStep : Number(currentStep) + 1,
        }
      );
      const isSaveLater = saveType !== 'save and proceed';
      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              applicants: updatedApplicants,
              backgroundDeclaration: false,
              fundDeclaration: false,
              status:
                !hasPOA &&
                AMC_APPROVER_CHECK_FOR_INDIVIDUAL &&
                status !== 'draft' &&
                applyRoleBasedStatus(role)
                  ? 'sent_to_amc_approver'
                  : status,
              currentStep: 4,
            },
            applicationId: id,
            ...(isSaveLater && {
              toastMessage: '',
            }),
          })
        );
      } else if (checkApplication) {
        if (isSaveLater) {
          enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        }
      }
      const routeToSaveForLater = isSaveLater && saveForLater(role, id, applicant1ReferenceId);
      !isSaveLater
        ? history.push('fatca', { id, applicant1ReferenceId })
        : routeToSaveForLater && history.push(routeToSaveForLater);
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e));
      console.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={AdditionalKycDetailsValidation}
      onSubmit={handleSubmit}
      enableReinitialize={true}>
      {({ handleSubmit, values, setValues, setFieldValue }) => (
        <Grid
          container
          rowSpacing={1}
          sx={{
            width: '100%',
            ml: 0,
            '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
          }}
          component="form"
          noValidate
          onSubmit={handleSubmit}>
          {values.applicants.map((applicant: any, idx: number) => (
            <>
              <SubHeading
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                Additional KYC Details of {getApplicantName(idx + 1, true)} Applicant
              </SubHeading>
              <AdditionalKycDetails
                data={applicant.additional_kyc as IndividualAdditionalKYC[]}
                values={values}
                setValues={setValues}
                applicantIndex={idx}
              />
            </>
          ))}
          {/* <SubHeading
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            Declaration on Origin(s) of Funds
          </SubHeading>
          <Grid item xs={12}>
            <MFCheckbox
              name={`fundDeclaration`}
              label={`I/We confirm that the funds transferred are sourced from my/our personal/business assets and/or from benefits of transactions due to me/us of which are known to me/us, more specifically, from (Please Select Source of Funds).`}
              sx={{ display: 'flex', alignItems: 'flex-start', mt: 2 }}
            />
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <MFCheckbox
              name={`backgroundDeclaration`}
              label="It is hereby declared and confirmed that:"
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: 'rgba(0,0,0,0.7)',
                '.MuiTypography-root': {
                  fontSize: 15,
                  fontWeight: 500,
                },
              }}
            />
            <AdditionalKycDeclarationForbackgroundText />
          </Grid> */}
          {!!application?.applicants.length && (
            <ProceedSaveLater
              saveLater={() => setFieldValue('saveType', 'save for later')}
              saveAndProceed={() => setFieldValue('saveType', 'save and proceed')}
              loader={loading}
              clickedButton={values.saveType}
            />
          )}
        </Grid>
      )}
    </Formik>
  );
}
